import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { formatCurrency, formatPercent } from '../utils/calculations';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [sortBy, setSortBy] = useState('savedAt');
  const [filterStrategy, setFilterStrategy] = useState('all');

  // Recent analyses from localStorage
  const [recentAnalyses, setRecentAnalyses] = useState([]);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('proparv_deals') || '[]');
    setDeals(saved);
    const storageKey = `proparv_analyzed_${user?.id || 'guest'}`;
    const analyses = JSON.parse(localStorage.getItem(storageKey) || '[]');
    setRecentAnalyses(analyses);
  }, []);

  const deleteAnalysis = (id) => {
    const updated = recentAnalyses.filter(r => r.id !== id);
    setRecentAnalyses(updated);
    localStorage.setItem(`proparv_analyzed_${user?.id || 'guest'}`, JSON.stringify(updated));
  };

  const deleteDeal = (id) => {
    const updated = deals.filter(d => d.id !== id);
    setDeals(updated);
    localStorage.setItem('proparv_deals', JSON.stringify(updated));
  };

  const clearAll = () => {
    if (window.confirm('Delete all saved deals?')) {
      setDeals([]);
      localStorage.removeItem('proparv_deals');
    }
  };

  const strategyLabels = {
    flip: 'Fix & Flip', hold: 'Buy & Hold', brrrr: 'BRRRR',
    wholesale: 'Wholesale', airbnb: 'Airbnb'
  };
  const strategyColors = {
    flip: '#f59e0b', hold: '#10b981', brrrr: '#2563eb',
    wholesale: '#8b5cf6', airbnb: '#ef4444'
  };

  const filteredDeals = deals
    .filter(d => filterStrategy === 'all' || d.strategy === filterStrategy)
    .sort((a, b) => {
      if (sortBy === 'savedAt') return new Date(b.savedAt) - new Date(a.savedAt);
      if (sortBy === 'score') return (b.dealScore || 0) - (a.dealScore || 0);
      if (sortBy === 'roi') return (b.roi || 0) - (a.roi || 0);
      if (sortBy === 'profit') return (b.profit || 0) - (a.profit || 0);
      return 0;
    });

  const totalProfit = deals.reduce((s, d) => s + (d.profit || 0), 0);
  const bestDeal = deals.reduce((best, d) => (!best || (d.profit || 0) > (best.profit || 0)) ? d : best, null);

  return (
    <div className="page">
      <div className="container page-content">
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 800, marginBottom: 6 }}>
            My Deal Dashboard
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Welcome back, {user?.name || user?.email}. You have {deals.length} saved deal{deals.length !== 1 ? 's' : ''}.
          </p>
        </div>

        {/* Summary Stats */}
        {deals.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
            {[
              { label: 'Total Deals', value: deals.length, color: 'var(--primary-light)' },
              { label: 'Total Projected Profit', value: formatCurrency(totalProfit), color: totalProfit > 0 ? 'var(--success)' : 'var(--danger)' },
              { label: 'Best Deal', value: bestDeal ? bestDeal.address?.split(',')[0] || '—' : '—', color: 'var(--warning)' }
            ].map(stat => (
              <div key={stat.label} className="card" style={{ textAlign: 'center' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: stat.color, margin: '0 auto 12px' }} />
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: stat.color, marginBottom: 4 }}>{stat.value}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Controls */}
        {deals.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* Filter */}
              <select
                value={filterStrategy}
                onChange={e => setFilterStrategy(e.target.value)}
                className="input"
                style={{ width: 'auto', paddingRight: 32 }}
              >
                <option value="all">All Strategies</option>
                {Object.entries(strategyLabels).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="input"
                style={{ width: 'auto', paddingRight: 32 }}
              >
                <option value="savedAt">Sort: Most Recent</option>
                <option value="score">Sort: Deal Score</option>
                <option value="roi">Sort: ROI %</option>
                <option value="profit">Sort: Profit</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => navigate('/analyze')} className="btn btn-primary btn-sm">
                + Analyze New Deal
              </button>
              <button onClick={clearAll} className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}>
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* ── Recent Analyses ── */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: 2 }}>Recent Analyses</h2>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>
                Last {recentAnalyses.length} propert{recentAnalyses.length === 1 ? 'y' : 'ies'} analyzed — up to 20 stored
              </p>
            </div>
            <button onClick={() => navigate('/analyze')} className="btn btn-primary btn-sm">+ Analyze Property</button>
          </div>

          {recentAnalyses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: 'var(--bg-card)', borderRadius: 12, border: '2px dashed var(--border)' }}>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>
                No analyses yet. Go to <strong>Analyze</strong> and run your first deal analysis — it will appear here automatically.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
              {recentAnalyses.map(p => (
                <div key={p.id} className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.address} referrerPolicy="no-referrer"
                      style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: 140, background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>No photo</div>
                  )}
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, lineHeight: 1.3 }}>
                      {p.address}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>ARV</span>
                      <span style={{ fontSize: '0.92rem', fontWeight: 800, color: 'var(--primary-light)' }}>
                        {p.arv ? formatCurrency(p.arv) : '—'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 10 }}>
                      {p.beds && <span>{p.beds} bd</span>}
                      {p.baths && <span>· {p.baths} ba</span>}
                      {p.sqft && <span>· {Number(p.sqft).toLocaleString()} sqft</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => navigate(`/analyze?address=${encodeURIComponent(p.address)}`)}
                        className="btn btn-primary btn-sm"
                        style={{ flex: 1, fontSize: '0.75rem' }}
                      >
                        Re-Analyze
                      </button>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 8 }}>
                      {new Date(p.analyzedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteAnalysis(p.id)}
                    title="Remove"
                    style={{
                      position: 'absolute', top: 8, right: 8,
                      background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff',
                      borderRadius: 6, width: 26, height: 26, cursor: 'pointer',
                      fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backdropFilter: 'blur(4px)'
                    }}
                  >✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deals List */}
        {filteredDeals.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {filteredDeals.map(deal => {
              const sColor = strategyColors[deal.strategy] || 'var(--primary-light)';
              return (
                <div
                  key={deal.id}
                  className="card"
                  style={{ padding: '20px 24px', transition: 'all 0.2s' }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    {/* Left: Address + meta */}
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                          {deal.address || 'Unknown Address'}
                        </h3>
                        <span style={{
                          background: `${sColor}22`, border: `1px solid ${sColor}44`,
                          color: sColor, borderRadius: 999, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700
                        }}>
                          {strategyLabels[deal.strategy] || deal.strategy}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        Saved {new Date(deal.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {deal.beds && ` • ${deal.beds}bd/${deal.baths}ba`}
                        {deal.sqft && ` • ${(deal.sqft).toLocaleString()} sqft`}
                      </div>
                    </div>

                    {/* Center: Metrics */}
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                      {[
                        { label: 'ARV', value: formatCurrency(deal.arv), color: 'var(--primary-light)' },
                        { label: 'Purchase', value: formatCurrency(deal.purchasePrice), color: 'var(--text-primary)' },
                        { label: 'Profit', value: formatCurrency(deal.profit), color: (deal.profit || 0) > 0 ? '#10b981' : '#ef4444' },
                        { label: 'ROI', value: formatPercent(deal.roi), color: (deal.roi || 0) >= 15 ? '#10b981' : '#f59e0b' }
                      ].map(m => (
                        <div key={m.label} style={{ textAlign: 'center', minWidth: 60 }}>
                          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: m.color }}>{m.value}</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Right: Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {/* Buttons */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button
                          onClick={() => navigate(`/analyze?address=${encodeURIComponent(deal.address || '')}`)}
                          className="btn btn-outline btn-sm"
                        >
                          View →
                        </button>
                        <button
                          onClick={() => deleteDeal(deal.id)}
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--danger)', borderColor: 'var(--danger)', opacity: 0.7 }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{
            textAlign: 'center', padding: '80px 20px',
            background: 'var(--bg-card)', borderRadius: 16, border: '2px dashed var(--border)'
          }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: 8 }}>No Saved Deals Yet</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 28, maxWidth: 360, margin: '0 auto 28px' }}>
              Analyze a property and click "Save Deal" to track it here.
            </p>
            <button onClick={() => navigate('/analyze')} className="btn btn-primary btn-lg">
              Analyze Your First Deal
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
